import React, { useEffect, useState } from 'react'

import cx from 'classnames'

import { CircleLoader, Div, H5 } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'

import { DeserializedSavedFilter } from 'api'
import SavedFiltersActionMenu from 'components/SavedFiltersActionMenu'
import { isEU } from 'config'
import { INACTIVE_FILTER } from 'hooks/useSavedFilters'

import actionStyles from '../../../components/SavedFiltersActionMenu/styles.module.css'
import styles from './styles.module.css'

const LOCAL_STORAGE_PREFIX = 'CCRMFeed.toggle.'

interface CCRMSavedViewsSidebarProps {
  savedFilters?: DeserializedSavedFilter[]
  activeFilterId?: number
  defaultFilterId?: number
  setActiveFilterId: (id: number | undefined) => void
  updateDefaultFilterId: (id: number) => void
  removeSavedFilter: (id: number) => void
  onOpenEditModal: () => void
  onCreateSavedFilter: () => void
}

const CCRMSavedViewsSidebar: React.FC<CCRMSavedViewsSidebarProps> = ({
  savedFilters,
  activeFilterId,
  defaultFilterId,
  setActiveFilterId,
  updateDefaultFilterId,
  removeSavedFilter,
  onOpenEditModal,
  onCreateSavedFilter
}) => {
  const highlightAllAccounts = activeFilterId === INACTIVE_FILTER
  const [showAccountsSection, setShowAccountsSection] = useState<boolean>(true)
  const [showPrivateFiltersSection, setShowPrivateFiltersSection] = useState<boolean>(true)
  const [showSharedFiltersSection, setShowSharedFiltersSection] = useState<boolean>(true)

  useEffect(() => {
    setShowAccountsSection(
      localStorage.getItem(LOCAL_STORAGE_PREFIX + 'showAccountsSection') !== 'false'
    )
    setShowPrivateFiltersSection(
      localStorage.getItem(LOCAL_STORAGE_PREFIX + 'showPrivateFiltersSection') !== 'false'
    )
    setShowSharedFiltersSection(
      localStorage.getItem(LOCAL_STORAGE_PREFIX + 'showSharedFiltersSection') !== 'false'
    )
  }, [])

  useEffect(() => {
    localStorage.setItem(
      LOCAL_STORAGE_PREFIX + 'showAccountsSection',
      showAccountsSection.toString()
    )
    localStorage.setItem(
      LOCAL_STORAGE_PREFIX + 'showPrivateFiltersSection',
      showPrivateFiltersSection.toString()
    )
    localStorage.setItem(
      LOCAL_STORAGE_PREFIX + 'showSharedFiltersSection',
      showSharedFiltersSection.toString()
    )
  }, [showAccountsSection, showPrivateFiltersSection, showSharedFiltersSection])

  const privateFilters = savedFilters
    ?.filter(filter => !filter.shared)
    ?.sort((a, b) => a.name.localeCompare(b.name))
  const sharedFilters = savedFilters
    ?.filter(filter => filter.shared)
    ?.sort((a, b) => a.name.localeCompare(b.name))

  const showToolTip = ({ name }: DeserializedSavedFilter): boolean => name.length > 19

  return (
    <Div className={styles.ActivitiesSidebar}>
      <Div className={styles.SidebarTopSection}>
        <Div className={styles.FilterSection}>
          <Div
            className={styles.FilterSectionTitle}
            onClick={() => setShowAccountsSection(!showAccountsSection)}>
            <Div>Accounts</Div>
            <Icon
              name={showAccountsSection ? 'chevron-up' : 'chevron-down'}
              height="6px"
              width="9px"
            />
          </Div>
          {showAccountsSection && (
            <>
              <Div
                onClick={() => setActiveFilterId(undefined)}
                className={cx('ActivitiesViewItem', actionStyles.ActivitiesViewItem, {
                  CurrentViewItem: highlightAllAccounts,
                  [actionStyles.CurrentViewItem]: highlightAllAccounts
                })}>
                <H5>All accounts</H5>
              </Div>
              {!isEU && (
                <a target="_blank" href="/live">
                  <Div
                    className={cx(
                      'ActivitiesViewItem',
                      actionStyles.ActivitiesViewItem,
                      actionStyles.ActivitiesLiveViewItem
                    )}>
                    <H5>Live view</H5>
                    <Icon height={16} width={16} name="open-new-tab" />
                  </Div>
                </a>
              )}
            </>
          )}
        </Div>
        {(!privateFilters || privateFilters.length > 0) && (
          <Div className={styles.FilterSection}>
            <Div
              className={styles.FilterSectionTitle}
              onClick={() => setShowPrivateFiltersSection(!showPrivateFiltersSection)}>
              <Div>Your saved filters</Div>
              <Icon
                name={showPrivateFiltersSection ? 'chevron-up' : 'chevron-down'}
                height="6px"
                width="9px"
              />
            </Div>
            {showPrivateFiltersSection &&
              (privateFilters === undefined ? (
                <CircleLoader className={styles.Loader} />
              ) : (
                privateFilters.map((filter: DeserializedSavedFilter) => (
                  <SavedFiltersActionMenu
                    key={filter.id}
                    savedFilter={filter}
                    isActive={activeFilterId === filter.id}
                    defaultFilterId={defaultFilterId}
                    onOpenEditModal={onOpenEditModal}
                    updateDefaultFilterId={updateDefaultFilterId}
                    removeSavedFilter={removeSavedFilter}
                    setActiveFilterId={setActiveFilterId}
                    includeUpdateDefaultFilter={false}
                    showToolTip={showToolTip(filter)}
                  />
                ))
              ))}
          </Div>
        )}
        {(!sharedFilters || sharedFilters.length > 0) && (
          <Div className={styles.FilterSection}>
            <Div
              className={styles.FilterSectionTitle}
              onClick={() => setShowSharedFiltersSection(!showSharedFiltersSection)}>
              <Div>Shared saved filters</Div>
              <Icon
                name={showSharedFiltersSection ? 'chevron-up' : 'chevron-down'}
                height="6px"
                width="9px"
              />
            </Div>
            {showSharedFiltersSection &&
              (sharedFilters === undefined ? (
                <CircleLoader className={styles.Loader} />
              ) : (
                sharedFilters.map((filter: DeserializedSavedFilter) => (
                  <SavedFiltersActionMenu
                    key={filter.id}
                    savedFilter={filter}
                    isActive={activeFilterId === filter.id}
                    defaultFilterId={defaultFilterId}
                    onOpenEditModal={onOpenEditModal}
                    updateDefaultFilterId={updateDefaultFilterId}
                    removeSavedFilter={removeSavedFilter}
                    setActiveFilterId={setActiveFilterId}
                    includeUpdateDefaultFilter={false}
                    showToolTip={showToolTip(filter)}
                  />
                ))
              ))}
          </Div>
        )}

        <hr className={styles.Separator} />
        <Div
          className={cx(styles.CreateFilterButton, actionStyles.ActivitiesViewItem)}
          onClick={onCreateSavedFilter}>
          <Icon name="plus" height={12} width={12} />
          <Div className={styles.CreateFilterText}>Create saved filter</Div>
        </Div>
      </Div>
    </Div>
  )
}

export default CCRMSavedViewsSidebar
